import React, {useMemo, useRef} from "react";
import {Table} from "reactstrap";
import HelpIcon from "../Help/HelpIcon";
import {Line} from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  Filler,
  TimeScale,
  ChartOptions,
  ChartData
} from "chart.js";
import "chartjs-adapter-date-fns";
import annotationPlugin from "chartjs-plugin-annotation";

import {chartHeight, maxCrowdedness} from "../../constants";
import colors from "../../styles/colors";
import {useChartTheme} from "../../contexts/ChartThemeContext";
import {ChartErrorBoundary} from "../Charts/ChartErrorBoundary";
import {createChartDataProcessor} from "../../utils/chartDataProcessor";
import {alignDataToTimeline} from "../../utils/dataAlignment";
import {createTickFormatter} from "../../utils/chartTicks";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  ChartLegend,
  Filler,
  TimeScale,
  annotationPlugin
);

function getCrowdednessScore(avgStc: number, avgCts: number, maxCrowdedness: number) {
  return Math.min(1, Math.round(((avgStc + avgCts) / maxCrowdedness) * 100) / 100);
}

interface UdpReportProps {
  points: Array<[number, number, number, number, number]>; // [time, stc, rxerrors, cts, txerrors]
  avgStc: number;
  avgCts: number;
  avgStcPlusAvgCts: number;
  maxStc: number;
  maxCts: number;
  maxStcPlusMaxCts: number;
  sumRxErrors: number;
  sumTxErrors: number;
  axisSeries?: {
    name: string;
    data: Array<{timestamp: number; value: number}>;
    timeRange?: {start: number; end: number};
    ticks?: Array<{timestamp: number; label: string}>;
  };
}

const UdpReport: React.FC<UdpReportProps> = (props) => {
  const chartRef = useRef<ChartJS<"line", {x: number; y: number}[], unknown>>(null);
  const {getThemeColors} = useChartTheme();
  const dataProcessor = createChartDataProcessor();

  if (!props.points || !props.points.length) {
    return <></>;
  }

  // Create tick formatter with consistent formatting
  // Note: Data is pre-localized in parent ChartsReport, so we pass 0 for timezone offset
  const tickFormatter = useMemo(() => {
    return createTickFormatter(0);
  }, []);

  const chartData = useMemo(() => {
    if (!props.points || props.points.length === 0) {
      return [];
    }

    // If we have axisSeries, use it for proper timeline alignment
    if (props.axisSeries?.data) {
      try {
        // UDP report uses 1-minute windows with max aggregation (matches original pondjs implementation)
        // Original: windowSize: "1m", aggregation: { stc: {stc: max()}, cts: {cts: max()}, etc. }

        // Process UDP data using the shared timeline with proper 1-minute max aggregation
        // Process each metric separately to preserve individual stc/cts values for Area charts
        const stcData = alignDataToTimeline(
          props.axisSeries.data, // Use shared timeline from ChartsReport
          props.points.map((point) => [point[0], point[1]]), // [timestamp, stc]
          "1m", // 1 minute windows (matches original)
          "max" // Max aggregation (matches original pondjs max() function)
        );

        const ctsData = alignDataToTimeline(
          props.axisSeries.data, // Use shared timeline from ChartsReport
          props.points.map((point) => [point[0], point[3]]), // [timestamp, cts]
          "1m", // 1 minute windows (matches original)
          "max" // Max aggregation (matches original pondjs max() function)
        );

        const rxErrorsData = alignDataToTimeline(
          props.axisSeries.data,
          props.points.map((point) => [point[0], point[2]]), // [timestamp, rxerrors]
          "1m",
          "max"
        );

        const txErrorsData = alignDataToTimeline(
          props.axisSeries.data,
          props.points.map((point) => [point[0], point[4]]), // [timestamp, txerrors]
          "1m",
          "max"
        );

        // Convert to chart format with individual values for proper Area chart rendering
        const chartFormatData = stcData.map((point, index) => {
          return {
            timestamp: point.timestamp,
            stc: point.value || 0,
            cts: ctsData[index]?.value || 0,
            rxerrors: rxErrorsData[index]?.value || 0,
            txerrors: txErrorsData[index]?.value || 0,
            combined: (point.value || 0) + (ctsData[index]?.value || 0)
          };
        });

        // Data is already optimally sampled by the preset system
        return chartFormatData;
      } catch {
        // Fall back to original method
      }
    }

    // Fallback: Original processing method (for backward compatibility)
    const processedData = props.points.map((point) => ({
      timestamp: point[0],
      stc: point[1],
      rxerrors: point[2],
      cts: point[3],
      txerrors: point[4],
      combined: point[1] + point[3] // stc + cts
    }));

    // Data is already optimally sampled by the preset system
    return processedData;
  }, [props.points, props.axisSeries, dataProcessor]);

  const statistics = useMemo(() => {
    const udpSeries = {
      avg: (field: string) => {
        switch (field) {
          case "stc":
            return props.avgStc;
          case "cts":
            return props.avgCts;
          default:
            return 0;
        }
      },
      max: (field: string) => {
        switch (field) {
          case "stc":
            return props.maxStc;
          case "cts":
            return props.maxCts;
          default:
            return 0;
        }
      },
      sum: (field: string) => {
        switch (field) {
          case "rxerrors":
            return props.sumRxErrors;
          case "txerrors":
            return props.sumTxErrors;
          default:
            return 0;
        }
      }
    };
    return udpSeries;
  }, [props]);

  const themeColors = getThemeColors();

  // Prepare data for Chart.js
  const preparedChartData = useMemo(() => {
    // Create stacked area data for STC and CTS
    const stackedData = chartData.map((d) => ({
      x: d.timestamp,
      y: d.stc,
      cts: d.cts
    }));

    // Create separate datasets for errors (scatter points)
    const rxErrorData = chartData.filter((d) => d.rxerrors > 0).map((d) => ({x: d.timestamp, y: d.rxerrors}));

    const txErrorData = chartData.filter((d) => d.txerrors > 0).map((d) => ({x: d.timestamp, y: d.txerrors}));

    return {
      stackedData,
      rxErrorData,
      txErrorData
    };
  }, [chartData]);

  // Chart.js data configuration
  const data: ChartData<"line", {x: number; y: number}[]> = {
    datasets: [
      {
        label: "Server to Client",
        data: preparedChartData.stackedData,
        borderColor: colors.yellow,
        // Hex colors use #RRGGBBAA format where AA is the alpha channel (transparency)
        backgroundColor: colors.yellow + "CC", // 80% opacity for stacked area
        fill: true,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1,
        stack: "stack0"
      },
      {
        label: "Client to Server",
        data: preparedChartData.stackedData.map((d) => ({x: d.x, y: (d as any).cts})),
        borderColor: colors.magenta,
        backgroundColor: colors.magenta + "CC", // 80% opacity for stacked area
        fill: "-1", // Fill to previous dataset (creates stacked effect)
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1,
        stack: "stack0"
      },
      ...(preparedChartData.rxErrorData.length > 0
        ? [
            {
              label: "Receive Errors",
              data: preparedChartData.rxErrorData,
              backgroundColor: colors.fiestared,
              borderColor: colors.fiestared,
              pointRadius: 4,
              pointHoverRadius: 6,
              showLine: false // Scatter plot for errors
            }
          ]
        : []),
      ...(preparedChartData.txErrorData.length > 0
        ? [
            {
              label: "Transmit Errors",
              data: preparedChartData.txErrorData,
              backgroundColor: colors.salmonpink,
              borderColor: colors.salmonpink,
              pointRadius: 4,
              pointHoverRadius: 6,
              showLine: false // Scatter plot for errors
            }
          ]
        : [])
    ]
  };

  // Chart.js options configuration
  const options: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false, // Disable animations for better performance
      interaction: {
        mode: "index" as const,
        intersect: false
      },
      onClick: (event, activeElements, chart) => {
        // Double click to reset zoom
        const now = Date.now();
        (chart as any).lastClickTime = now;
      },
      plugins: {
        legend: {
          display: true,
          position: "top" as const,
          labels: {
            color: themeColors.text,
            usePointStyle: true,
            padding: 15
          }
        },
        tooltip: {
          enabled: true,
          backgroundColor: themeColors.tooltip.background,
          borderColor: themeColors.tooltip.border,
          borderWidth: 1,
          titleColor: themeColors.tooltip.text,
          bodyColor: themeColors.tooltip.text,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: (context) => {
              const timestamp = context[0]?.parsed?.x;
              return timestamp ? new Date(timestamp).toLocaleTimeString() : "";
            },
            label: (context) => {
              const label = context.dataset.label || "";
              const value = context.parsed?.y;

              // For stacked data, show both STC and CTS
              if (label === "Server to Client" || label === "Client to Server") {
                const xValue = context.parsed?.x;
                if (xValue !== null && xValue !== undefined) {
                  const dataPoint = chartData.find((d) => Math.abs(d.timestamp - xValue) < 1000);
                  if (dataPoint) {
                    if (label === "Server to Client") {
                      return `STC: ${dataPoint.stc}`;
                    } else {
                      return `CTS: ${dataPoint.cts}`;
                    }
                  }
                }
              }

              return value !== null && value !== undefined ? `${label}: ${Math.round(value)}` : `${label}: No data`;
            }
          }
        },
        annotation: {
          annotations: {
            avgStcLine: {
              type: "line" as const,
              scaleID: "y",
              value: props.avgStc,
              borderColor: colors.yellow,
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                display: true,
                content: `Avg StC (${Math.round(props.avgStc)})`,
                position: "end" as const,
                backgroundColor: colors.yellow,
                color: "black",
                font: {
                  size: 12,
                  weight: "bold" as const
                },
                padding: 4
              }
            },
            avgCtsLine: {
              type: "line" as const,
              scaleID: "y",
              value: props.avgCts,
              borderColor: colors.magenta,
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                display: true,
                content: `Avg CtS (${Math.round(props.avgCts)})`,
                position: "end" as const,
                backgroundColor: colors.magenta,
                color: "white",
                font: {
                  size: 12,
                  weight: "bold" as const
                },
                padding: 4
              }
            },
            // Theoretical max line (maxCrowdedness = 3500)
            theoreticalMax: {
              type: "line" as const,
              scaleID: "y",
              value: maxCrowdedness,
              borderColor: colors.fiestared,
              borderWidth: 3,
              borderDash: [10, 5],
              label: {
                display: true,
                content: `Theoretical Max (${maxCrowdedness})`,
                position: "start" as const,
                backgroundColor: colors.fiestared,
                color: "white",
                font: {
                  size: 11,
                  weight: "bold" as const
                },
                padding: 3
              }
            },
            // Crowding tier lines - each represents the threshold for that tier
            // Fondo threshold: 80% of max = 2800
            fondoLine: {
              type: "line" as const,
              scaleID: "y",
              value: maxCrowdedness * 0.8,
              borderColor: colors.fiestared + "80",
              borderWidth: 1,
              borderDash: [3, 3],
              label: {
                display: true,
                content: "Fondo (80%)",
                position: "start" as const,
                backgroundColor: colors.fiestared + "CC",
                color: "white",
                font: {
                  size: 10
                },
                padding: 2
              }
            },
            // Packed threshold: 60% of max = 2100
            packedLine: {
              type: "line" as const,
              scaleID: "y",
              value: maxCrowdedness * 0.6,
              borderColor: colors.orange + "80",
              borderWidth: 1,
              borderDash: [3, 3],
              label: {
                display: true,
                content: "Packed (60%)",
                position: "start" as const,
                backgroundColor: colors.orange + "CC",
                color: "white",
                font: {
                  size: 10
                },
                padding: 2
              }
            },
            // Crowded threshold: 40% of max = 1400
            crowdedLine: {
              type: "line" as const,
              scaleID: "y",
              value: maxCrowdedness * 0.4,
              borderColor: colors.yellow + "80",
              borderWidth: 1,
              borderDash: [3, 3],
              label: {
                display: true,
                content: "Crowded (40%)",
                position: "start" as const,
                backgroundColor: colors.yellow + "CC",
                color: "black",
                font: {
                  size: 10
                },
                padding: 2
              }
            },
            // Company threshold: 20% of max = 700
            companyLine: {
              type: "line" as const,
              scaleID: "y",
              value: maxCrowdedness * 0.2,
              borderColor: colors.brand + "80",
              borderWidth: 1,
              borderDash: [3, 3],
              label: {
                display: true,
                content: "Company (20%)",
                position: "start" as const,
                backgroundColor: colors.brand + "CC",
                color: "white",
                font: {
                  size: 10
                },
                padding: 2
              }
            }
          }
        }
      },
      scales: {
        x: {
          type: "linear",
          display: true,
          title: {
            display: false
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            },
            maxTicksLimit: 20,
            autoSkip: true,
            autoSkipPadding: 10,
            callback: function (value) {
              return tickFormatter(value);
            }
          },
          min: props.axisSeries?.timeRange?.start,
          max: props.axisSeries?.timeRange?.end
        },
        y: {
          type: "linear" as const,
          stacked: true,
          display: true,
          position: "left" as const,
          title: {
            display: true,
            text: "UDP Packets",
            color: themeColors.text
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            }
          },
          min: 0
        }
      }
    }),
    [themeColors, props.avgStc, props.avgCts, props.axisSeries, tickFormatter, chartData]
  );

  const crowdingScore: number = getCrowdednessScore(statistics.avg("stc"), statistics.avg("cts"), maxCrowdedness);
  const crowdingScoreStr: string = `${Math.round(crowdingScore * 100)}%`;

  let crowding: string = "";

  // these tiers are duplicated in the backend ETL jobs. If you change this, make sure to update in both places
  if (crowdingScore >= 0.8) {
    crowding = "Fondo";
  } else if (crowdingScore >= 0.6) {
    crowding = "Packed";
  } else if (crowdingScore >= 0.4) {
    crowding = "Crowded";
  } else if (crowdingScore >= 0.2) {
    crowding = "Company";
  } else {
    crowding = "Solo";
  }

  const crowdingCssClassName: string = crowding.toLowerCase();

  const maxStc: number = Math.round(statistics.max("stc"));
  const avgStc: number = Math.round(statistics.avg("stc"));
  const sumTxerrors: number = Math.round(statistics.sum("txerrors"));
  const sumRxerrors: number = Math.round(statistics.sum("rxerrors"));
  const maxCts: number = Math.round(statistics.max("cts"));
  const avgCts: number = Math.round(statistics.avg("cts"));

  return (
    <>
      <h5>
        UDP Network Metrics
        <HelpIcon topicId="udpNetworkPackets" />
      </h5>

      <Table responsive>
        <thead>
          <tr>
            <th style={{width: "12%"}}>
              Max StC
              <HelpIcon topicId="udpMaxStc" />
            </th>
            <th style={{width: "12%"}}>
              Avg StC
              <HelpIcon topicId="udpAvgStc" />
            </th>
            <th style={{width: "12%"}}>
              Max CtS
              <HelpIcon topicId="udpMaxCtS" />
            </th>
            <th style={{width: "12%"}}>
              Avg CtS
              <HelpIcon topicId="udpAvgCtS" />
            </th>
            <th style={{width: "12%"}}>
              Rx Err
              <HelpIcon topicId="udpRxErr" />
            </th>
            <th style={{width: "12%"}}>
              Tx Err
              <HelpIcon topicId="udpTxErr" />
            </th>
            <th style={{width: "12%"}}>
              Crowding
              <HelpIcon topicId="udpCrowdedness" />
            </th>
            <th style={{width: "16%"}}>
              Crowding Tier
              <HelpIcon topicId="udpCrowdednessTier" />
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>
              <div className={"numbers stc"}>{maxStc}</div>
            </td>
            <td>
              <div className={"numbers stc avg"}>{avgStc}</div>
            </td>
            <td>
              <div className={"numbers cts"}>{maxCts}</div>
            </td>
            <td>
              <div className={"numbers cts avg"}>{avgCts}</div>
            </td>
            <td>
              <div className={`numbers ${sumRxerrors > 0 ? "bad" : "good"}`}>
                {isNaN(sumRxerrors) ? 0 : sumRxerrors}
              </div>
            </td>
            <td>
              <div className={`numbers ${sumTxerrors > 0 ? "bad" : "good"}`}>
                {isNaN(sumTxerrors) ? 0 : sumTxerrors}
              </div>
            </td>
            <td>
              <div className={`numbers crowding ${crowdingCssClassName}`}>{crowdingScoreStr}</div>
            </td>
            <td>
              <div className={"numbers"}>{crowding}</div>
            </td>
          </tr>
        </tbody>
      </Table>

      <ChartErrorBoundary>
        <div style={{width: "100%", marginTop: "20px", marginBottom: "30px", position: "relative"}}>
          <div style={{display: "flex", justifyContent: "flex-end", marginBottom: "10px"}}></div>
          <div style={{width: "100%", height: chartHeight * 1.25, position: "relative"}}>
            <Line ref={chartRef} data={data} options={options} />
          </div>
        </div>
      </ChartErrorBoundary>
    </>
  );
};

export default UdpReport;
